import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import AppPage from "../components/AppPage";
import {
  listTasks, createTask, updateTask, deleteTask,
  markTaskDone, reopenTask,
  listTaskComments, addTaskComment,
  listCompanyUsers,
} from "../api";

const PRIORITY_KEY = ["low", "medium", "high"];
const PRIORITY_STYLE = {
  low:    { color: "#6c757d", bg: "#f1f3f6" },
  medium: { color: "#0d6efd", bg: "#e7f1ff" },
  high:   { color: "#dc3545", bg: "#fde2e4" },
};

const todayISO = () => new Date().toISOString().slice(0, 10);

function isOverdue(t) {
  return t.status === "open" && t.deadline && t.deadline < todayISO();
}

export default function Tasks() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [view, setView] = useState("list");
  const [scope, setScope] = useState("mine");
  const [statusFilter, setStatusFilter] = useState("");
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);

  const reload = useCallback(async () => {
    setBusy(true); setError("");
    try {
      const params = { scope };
      if (statusFilter) params.status = statusFilter;
      const [t, u] = await Promise.all([listTasks(params), listCompanyUsers()]);
      setTasks(t);
      setUsers(u);
    } catch (e) {
      setError(e.response?.data?.detail || "Не удалось загрузить задачи");
    } finally { setBusy(false); }
  }, [scope, statusFilter]);

  useEffect(() => { reload(); }, [reload]);

  const role = user?.role;
  const canAssignAnyone = role === "director" || role === "hr";
  const allowedAssignees = useMemo(() => {
    if (!users.length) return [];
    if (canAssignAnyone) return users;
    if (role === "manager") {
      return users.filter(u => u.user_id === user.user_id || u.manager_id === user.user_id);
    }
    // employee
    return users.filter(u => u.user_id === user.user_id);
  }, [users, role, canAssignAnyone, user]);

  const onSave = async (data) => {
    try {
      if (data.task_id) {
        await updateTask(data.task_id, data);
      } else {
        await createTask(data);
      }
      setEditing(null);
      await reload();
    } catch (e) {
      throw new Error(e.response?.data?.detail || "Ошибка сохранения");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm(t("tasks.delete_confirm"))) return;
    await deleteTask(id);
    await reload();
  };

  const onToggleDone = async (t) => {
    if (t.status === "open") await markTaskDone(t.task_id);
    else await reopenTask(t.task_id);
    await reload();
  };

  return (
    <AppPage testId="tasks-page" bodyScroll="scroll">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h4 className="fw-bold mb-0">
          <i className="bi bi-check2-square me-2 text-primary"></i>
          {t("tasks.title")}
        </h4>
        <button className="btn btn-primary" onClick={() => setEditing({})} data-testid="new-task-btn">
          <i className="bi bi-plus-lg me-1"></i>{t("tasks.new_task")}
        </button>
      </div>

      {/* Filters */}
      <div className="card card-soft mb-3"><div className="card-body py-3">
        <div className="row g-2 align-items-end">
          <div className="col-md-3 col-sm-6">
            <label className="form-label small mb-1">{t("tasks.scope")}</label>
            <select className="form-select form-select-sm" value={scope}
                    onChange={(e) => setScope(e.target.value)} data-testid="tasks-scope">
              <option value="mine">{t("tasks.scope_mine")}</option>
              <option value="assigned-by-me">{t("tasks.scope_by_me")}</option>
              <option value="subordinates">{role === "manager" ? t("tasks.scope_team") : t("common.all")}</option>
              {(canAssignAnyone) && <option value="all">{t("tasks.scope_all")}</option>}
            </select>
          </div>
          <div className="col-md-3 col-sm-6">
            <label className="form-label small mb-1">{t("common.status")}</label>
            <select className="form-select form-select-sm" value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)} data-testid="tasks-status-filter">
              <option value="">{t("common.all")}</option>
              <option value="open">{t("tasks.filter_open")}</option>
              <option value="done">{t("tasks.filter_done")}</option>
            </select>
          </div>
          <div className="col-md-6 d-flex justify-content-md-end gap-2">
            <div className="btn-group" role="group">
              <ViewBtn active={view === "list"} onClick={() => setView("list")} icon="list-task" label={t("tasks.view_list")} testid="view-list" />
              <ViewBtn active={view === "calendar"} onClick={() => setView("calendar")} icon="calendar-week" label={t("tasks.view_calendar")} testid="view-calendar" />
              <ViewBtn active={view === "kanban"} onClick={() => setView("kanban")} icon="kanban" label={t("tasks.view_kanban")} testid="view-kanban" />
            </div>
          </div>
        </div>
      </div></div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {busy && !tasks.length && <div className="text-muted small">{t("common.loading")}</div>}

      {!busy && tasks.length === 0 && (
        <div className="card card-soft"><div className="card-body text-center text-muted py-5">
          <i className="bi bi-inbox fs-1 d-block mb-2"></i>
          {t("tasks.empty_state")}
        </div></div>
      )}

      {tasks.length > 0 && view === "list" && (
        <ListView tasks={tasks} onEdit={setEditing} onToggle={onToggleDone} onDelete={onDelete} />
      )}
      {tasks.length > 0 && view === "calendar" && (
        <CalendarView tasks={tasks} onPick={setEditing} />
      )}
      {tasks.length > 0 && view === "kanban" && (
        <KanbanView tasks={tasks} onEdit={setEditing} onToggle={onToggleDone} />
      )}

      {editing && (
        <TaskModal
          initial={editing}
          users={allowedAssignees}
          currentUser={user}
          onClose={() => setEditing(null)}
          onSave={onSave}
          onToggle={async (task) => { await onToggleDone(task); setEditing(null); }}
        />
      )}
    </AppPage>
  );
}

// =============================================================================
// View toggle button
// =============================================================================
function ViewBtn({ active, onClick, icon, label, testid }) {
  return (
    <button type="button"
            className={`btn btn-sm ${active ? "btn-primary" : "btn-outline-secondary"}`}
            onClick={onClick} data-testid={testid}>
      <i className={`bi bi-${icon} me-1`}></i>{label}
    </button>
  );
}

// =============================================================================
// List view
// =============================================================================
function ListView({ tasks, onEdit, onToggle, onDelete }) {
  return (
    <div className="d-flex flex-column gap-2" data-testid="tasks-list">
      {tasks.map(t => (
        <TaskRow key={t.task_id} task={t} onEdit={onEdit} onToggle={onToggle} onDelete={onDelete} />
      ))}
    </div>
  );
}

function TaskRow({ task, onEdit, onToggle, onDelete }) {
  const { t } = useTranslation();
  const p = PRIORITY_STYLE[task.priority] || PRIORITY_STYLE.medium;
  const overdue = isOverdue(task);
  const isDone = task.status === "done";
  return (
    <div className={`card card-soft ${isDone ? "border-success" : ""}`} data-testid={`task-${task.task_id}`}
         style={isDone ? { background: "#f3faf5" } : undefined}>
      <div className="card-body py-2 px-3">
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <div className="flex-grow-1" style={{ minWidth: 200, cursor: "pointer" }}
               onClick={() => onEdit(task)}>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {isDone && (
                <span className="badge bg-success" data-testid={`task-done-badge-${task.task_id}`}>
                  <i className="bi bi-check-circle-fill me-1"></i>{t("tasks.done")}
                </span>
              )}
              <span className="fw-semibold">{task.title}</span>
              <span className="badge" style={{ background: p.bg, color: p.color }}>
                {t(`priorities.${task.priority}`)}
              </span>
              {overdue && <span className="badge bg-danger">{t("tasks.overdue")}</span>}
              {task.comments_count > 0 && (
                <span className="badge bg-light text-dark">
                  <i className="bi bi-chat-left-text me-1"></i>{task.comments_count}
                </span>
              )}
            </div>
            <div className="small text-muted">
              {task.assignee_name}
              {task.deadline && <> · <i className="bi bi-calendar3 me-1"></i>{task.deadline}</>}
              {task.creator_name && task.creator_name !== task.assignee_name &&
                <> · {t("common.from").toLowerCase()} {task.creator_name}</>}
            </div>
          </div>
          <div className="d-flex gap-1 align-items-center">
            <button
              className={`btn btn-sm ${isDone ? "btn-outline-secondary" : "btn-success"}`}
              onClick={() => onToggle(task)}
              data-testid={`task-toggle-${task.task_id}`}
              title={isDone ? t("tasks.reopen") : t("tasks.mark_done")}>
              <i className={`bi ${isDone ? "bi-arrow-counterclockwise" : "bi-check-lg"} me-1`}></i>
              {isDone ? t("tasks.reopen") : t("tasks.mark_done")}
            </button>
            <button className="btn btn-sm btn-light" onClick={() => onEdit(task)} title={t("common.open")}>
              <i className="bi bi-pencil"></i>
            </button>
            <button className="btn btn-sm btn-light text-danger" onClick={() => onDelete(task.task_id)} title={t("common.delete")}>
              <i className="bi bi-trash"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Calendar view (по дням, 4 недели от сегодня)
// =============================================================================
function CalendarView({ tasks, onPick }) {
  const { t, i18n } = useTranslation();
  const [anchor, setAnchor] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - d.getDay() + (d.getDay() === 0 ? -6 : 1));
    return d;
  });
  const days = [];
  for (let i = 0; i < 28; i++) {
    const d = new Date(anchor); d.setDate(anchor.getDate() + i);
    days.push(d);
  }
  const byDay = useMemo(() => {
    const map = {};
    tasks.forEach(t => {
      if (!t.deadline) return;
      (map[t.deadline] ||= []).push(t);
    });
    return map;
  }, [tasks]);
  const noDeadline = tasks.filter(t => !t.deadline);

  const shift = (n) => {
    const d = new Date(anchor); d.setDate(d.getDate() + n); setAnchor(d);
  };
  const todayStr = todayISO();

  return (
    <div data-testid="tasks-calendar">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <button className="btn btn-sm btn-light" onClick={() => shift(-7)}>← {t("tasks.week_back")}</button>
        <span className="fw-semibold">
          {anchor.toLocaleDateString(i18n.language === "kk" ? "kk-KZ" : (i18n.language === "en" ? "en-US" : "ru-RU"),
            { day: "2-digit", month: "long" })}
          {" — "}
          {days[27].toLocaleDateString(i18n.language === "kk" ? "kk-KZ" : (i18n.language === "en" ? "en-US" : "ru-RU"),
            { day: "2-digit", month: "long", year: "numeric" })}
        </span>
        <button className="btn btn-sm btn-light" onClick={() => shift(7)}>{t("tasks.week_forward")} →</button>
      </div>
      <div className="row g-2">
        {["mon","tue","wed","thu","fri","sat","sun"].map(w => (
          <div key={w} className="col text-center small text-muted">{t(`weekdaysShort.${w}`)}</div>
        ))}
      </div>
      <div className="row g-2 mt-0">
        {days.map((d, i) => {
          const iso = d.toISOString().slice(0,10);
          const items = byDay[iso] || [];
          const isToday = iso === todayStr;
          const isWeekend = d.getDay() === 0 || d.getDay() === 6;
          return (
            <div key={i} className="col-md mb-2" style={{ flexBasis: `${100/7}%`, minWidth: 110 }}>
              <div className="card card-soft h-100" style={{
                borderColor: isToday ? "#0b3d91" : undefined,
                borderWidth: isToday ? 2 : 1,
                background: isWeekend ? "#fafafa" : "#fff",
              }}>
                <div className="card-body p-2">
                  <div className={`small fw-bold mb-1 ${isToday ? "text-primary" : "text-muted"}`}>
                    {d.getDate()}.{String(d.getMonth()+1).padStart(2,"0")}
                    {isToday && <span className="ms-1">· {t("common.today")}</span>}
                  </div>
                  {items.map(item => {
                    const p = PRIORITY_STYLE[item.priority] || PRIORITY_STYLE.medium;
                    const itemDone = item.status === "done";
                    return (
                      <div key={item.task_id}
                           onClick={() => onPick(item)}
                           className="rounded p-1 mb-1"
                           style={{
                             background: itemDone ? "#d1e7dd" : p.bg,
                             color: itemDone ? "#0a3622" : p.color,
                             cursor: "pointer",
                             fontSize: 11,
                             border: itemDone ? "1px solid #198754" : "none",
                           }}>
                        <div className="text-truncate fw-semibold">
                          {itemDone && <i className="bi bi-check-circle-fill me-1"></i>}
                          {item.title}
                        </div>
                        <div className="text-truncate" style={{ opacity: 0.7 }}>{item.assignee_name}</div>
                      </div>
                    );
                  })}
                  {items.length === 0 && (
                    <div className="text-center text-muted" style={{ fontSize: 11, opacity: 0.4 }}>—</div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {noDeadline.length > 0 && (
        <div className="mt-3">
          <h6 className="text-muted">{t("tasks.no_deadline")} ({noDeadline.length})</h6>
          <div className="d-flex flex-wrap gap-2">
            {noDeadline.map(item => {
              const p = PRIORITY_STYLE[item.priority] || PRIORITY_STYLE.medium;
              return (
                <span key={item.task_id}
                      onClick={() => onPick(item)}
                      className="badge"
                      style={{ background: p.bg, color: p.color, cursor: "pointer", fontSize: 12 }}>
                  {item.title}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Kanban view (2 columns: Open / Done)
// =============================================================================
function KanbanView({ tasks, onEdit, onToggle }) {
  const { t } = useTranslation();
  const open = tasks.filter(x => x.status === "open");
  const done = tasks.filter(x => x.status === "done");
  return (
    <div className="row g-3" data-testid="tasks-kanban">
      <KanbanColumn title={t("tasks.kanban_open")} count={open.length} tasks={open}
                    onEdit={onEdit} onToggle={onToggle} accent="#0b3d91" />
      <KanbanColumn title={t("tasks.kanban_done")} count={done.length} tasks={done}
                    onEdit={onEdit} onToggle={onToggle} accent="#198754" />
    </div>
  );
}

function KanbanColumn({ title, count, tasks, onEdit, onToggle, accent }) {
  const { t } = useTranslation();
  return (
    <div className="col-md-6">
      <div className="card card-soft h-100">
        <div className="card-header py-2 d-flex justify-content-between align-items-center"
             style={{ borderLeft: `4px solid ${accent}` }}>
          <strong>{title}</strong>
          <span className="badge bg-light text-dark">{count}</span>
        </div>
        <div className="card-body p-2" style={{ minHeight: 200 }}>
          {tasks.length === 0 && (
            <div className="text-center text-muted py-4 small">— —</div>
          )}
          {tasks.map(task => {
            const p = PRIORITY_STYLE[task.priority] || PRIORITY_STYLE.medium;
            const overdue = isOverdue(task);
            const isDone = task.status === "done";
            return (
              <div key={task.task_id}
                   className="card mb-2"
                   onClick={() => onEdit(task)}
                   style={{
                     cursor: "pointer",
                     borderLeft: `3px solid ${isDone ? "#198754" : p.color}`,
                     background: isDone ? "#f3faf5" : undefined,
                   }}
                   data-testid={`kanban-task-${task.task_id}`}>
                <div className="card-body py-2 px-3">
                  {isDone && (
                    <div className="mb-1">
                      <span className="badge bg-success small">
                        <i className="bi bi-check-circle-fill me-1"></i>{t("tasks.done")}
                      </span>
                    </div>
                  )}
                  <div className="fw-semibold mb-1">
                    {task.title}
                  </div>
                  <div className="small text-muted d-flex align-items-center justify-content-between flex-wrap gap-1">
                    <span>
                      <i className="bi bi-person me-1"></i>{task.assignee_name}
                    </span>
                    {task.deadline && (
                      <span className={overdue ? "text-danger fw-semibold" : ""}>
                        <i className="bi bi-calendar3 me-1"></i>{task.deadline}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Create/edit modal with comments
// =============================================================================
function TaskModal({ initial, users, currentUser, onClose, onSave, onToggle }) {
  const { t, i18n } = useTranslation();
  const isNew = !initial.task_id;
  const [title, setTitle] = useState(initial.title || "");
  const [description, setDescription] = useState(initial.description || "");
  const [priority, setPriority] = useState(initial.priority || "medium");
  const [deadline, setDeadline] = useState(initial.deadline || "");
  const [assignee, setAssignee] = useState(initial.assignee_id || currentUser.user_id);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");

  useEffect(() => {
    if (initial.task_id) {
      listTaskComments(initial.task_id).then(setComments).catch(() => {});
    }
  }, [initial.task_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { setError(t("common.required")); return; }
    setBusy(true); setError("");
    try {
      await onSave({
        task_id: initial.task_id,
        title: title.trim(),
        description: description.trim(),
        priority,
        deadline: deadline || null,
        assignee_id: assignee,
      });
    } catch (e) {
      setError(e.message);
    } finally { setBusy(false); }
  };

  const sendComment = async () => {
    if (!newComment.trim() || !initial.task_id) return;
    const c = await addTaskComment(initial.task_id, newComment.trim());
    setComments([...comments, c]);
    setNewComment("");
  };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.45)" }}
         role="dialog" aria-modal="true" data-testid="task-modal">
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-check2-square me-2 text-primary"></i>
              {isNew ? t("tasks.new_task") : t("tasks.edit_task")}
              {!isNew && initial.status === "done" && (
                <span className="badge bg-success ms-2">{t("tasks.done")}</span>
              )}
            </h5>
            <button className="btn-close" onClick={onClose}></button>
          </div>
          <form onSubmit={submit}>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label small">{t("common.title")} *</label>
                  <input className="form-control" value={title}
                         onChange={(e) => setTitle(e.target.value)}
                         required maxLength={200}
                         data-testid="task-title" />
                </div>
                <div className="col-12">
                  <label className="form-label small">{t("common.description")}</label>
                  <textarea className="form-control" rows={3} value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            data-testid="task-description" />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">{t("tasks.assignee")} *</label>
                  <select className="form-select" value={assignee}
                          onChange={(e) => setAssignee(e.target.value)}
                          data-testid="task-assignee">
                    {users.map(u => (
                      <option key={u.user_id} value={u.user_id}>
                        {u.name}{u.user_id === currentUser.user_id ? ` (${t("common.profile").toLowerCase()})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label small">{t("common.priority")}</label>
                  <select className="form-select" value={priority}
                          onChange={(e) => setPriority(e.target.value)}
                          data-testid="task-priority">
                    {PRIORITY_KEY.map(k => (
                      <option key={k} value={k}>{t(`priorities.${k}`)}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label small">{t("common.deadline")}</label>
                  <input type="date" className="form-control" value={deadline || ""}
                         onChange={(e) => setDeadline(e.target.value)}
                         data-testid="task-deadline" />
                </div>
              </div>

              {!isNew && (
                <div className="mt-4">
                  <h6 className="text-muted small fw-semibold mb-2">
                    <i className="bi bi-chat-left-text me-1"></i>
                    {t("common.comments")} ({comments.length})
                  </h6>
                  <div className="d-flex flex-column gap-2 mb-2">
                    {comments.map(c => (
                      <div key={c.comment_id} className="p-2 rounded" style={{ background: "#f5f7fb" }}>
                        <div className="small fw-semibold">{c.user_name}</div>
                        <div className="small">{c.text}</div>
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          {new Date(c.created_at).toLocaleString(i18n.language === "kk" ? "kk-KZ" : (i18n.language === "en" ? "en-US" : "ru-RU"))}
                        </div>
                      </div>
                    ))}
                    {comments.length === 0 && (
                      <div className="small text-muted">{t("tasks.no_comments")}</div>
                    )}
                  </div>
                  <div className="input-group input-group-sm">
                    <input className="form-control" placeholder={t("tasks.comment_placeholder")}
                           value={newComment}
                           onChange={(e) => setNewComment(e.target.value)}
                           onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); sendComment(); } }}
                           data-testid="task-new-comment" />
                    <button type="button" className="btn btn-outline-primary"
                            onClick={sendComment} disabled={!newComment.trim()}
                            data-testid="task-send-comment">
                      <i className="bi bi-send"></i>
                    </button>
                  </div>
                </div>
              )}

              {error && <div className="alert alert-danger py-2 mt-3">{error}</div>}
            </div>
            <div className="modal-footer d-flex justify-content-between flex-wrap gap-2">
              <div>
                {!isNew && (
                  <button type="button"
                          className={`btn ${initial.status === "done" ? "btn-outline-secondary" : "btn-success"}`}
                          onClick={() => onToggle && onToggle(initial)}
                          data-testid="task-modal-toggle">
                    <i className={`bi ${initial.status === "done" ? "bi-arrow-counterclockwise" : "bi-check-circle"} me-1`}></i>
                    {initial.status === "done" ? t("tasks.reopen") : t("tasks.mark_done")}
                  </button>
                )}
              </div>
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-light" onClick={onClose}>{t("common.cancel")}</button>
                <button type="submit" className="btn btn-primary" disabled={busy} data-testid="task-save">
                  {busy ? t("common.loading") : (isNew ? t("common.create") : t("common.save"))}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
